import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  TextField,
  CircularProgress,
  Tabs,
  Tab,
  Box,
  Chip,
  Typography,
  Alert,
  Tooltip,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {useEffect, useState} from 'react';
import {OrgUser} from 'src/apis/autoGenerate/types/orgUser';
import {Tenant} from 'src/apis/autoGenerate/types/tenant';
import {
  orgUserServiceFind,
  tenantServiceFind,
  bulkAssignIntercomManagers,
  replaceIntercomManagers,
  apartmentServiceGetPermissions,
} from 'src/apis/autoGenerate/services';
import {
  IntercomManagerBulkAssignReq,
  IntercomManagerReplaceReq,
} from 'src/apis/autoGenerate/types/intercomDevice';
import message from 'src/components/Message';
import Modal from 'src/components/Modal';

/** Max call recipients per device (org + resident combined). */
export const MAX_CALL_RECIPIENTS = 10;

const RECIPIENTS_LIMIT_TOOLTIP = `You can select up to ${MAX_CALL_RECIPIENTS} recipients in total. Organization and resident users both count toward this limit.`;

type UserType = 'org' | 'tenant';
type SelectedUser = OrgUser | Tenant;

interface AssignManagerModalProps {
  open: boolean;
  onClose: () => void;
  onExited?: () => void;
  onConfirm: (users: SelectedUser[], userType: UserType, isReplace?: boolean) => void;
  onRefresh?: () => void; // Add this new prop for refreshing data
  orgId: string;
  deviceId?: string; // Add deviceId for direct API calls
  apartmentId?: string; // Add apartmentId to filter users by apartment permissions
  isEditMode?: boolean;
  currentManagers?: Array<{user: OrgUser | Tenant; userType: UserType}>; // Support multiple current managers
  allowMultiple?: boolean; // New prop to enable multiple selection
  enableBulkOperations?: boolean; // New prop to enable bulk API operations
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const {children, value, index, ...other} = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`user-tabpanel-${index}`}
      aria-labelledby={`user-tab-${index}`}
      {...other}
      style={{
        minHeight: value === index ? 'auto' : 0,
        transition: 'all 0.2s ease-in-out',
      }}
    >
      {value === index && (
        <Box sx={{pt: 2, opacity: value === index ? 1 : 0, transition: 'opacity 0.2s ease-in-out'}}>
          {children}
        </Box>
      )}
    </div>
  );
}

export default function AssignManagerModal({
  open,
  onClose,
  onExited,
  onConfirm,
  orgId,
  deviceId,
  apartmentId,
  isEditMode,
  currentManagers = [],
  allowMultiple = false,
  enableBulkOperations = false,
  onRefresh,
}: AssignManagerModalProps) {
  const [orgUsers, setOrgUsers] = useState<OrgUser[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loadingOrgUsers, setLoadingOrgUsers] = useState(false);
  const [loadingTenants, setLoadingTenants] = useState(false);
  const [selectedOrgUsers, setSelectedOrgUsers] = useState<OrgUser[]>([]);
  const [selectedTenants, setSelectedTenants] = useState<Tenant[]>([]);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);

  const title = isEditMode
    ? allowMultiple
      ? 'Edit Call Recipients'
      : 'Edit Call Recipient'
    : allowMultiple
    ? 'Assign Call Recipients'
    : 'Assign Call Recipient';

  const confirmButtonText = isEditMode ? 'Update' : 'Assign';

  // Set initial tab and selections based on current managers
  useEffect(() => {
    if (open && currentManagers.length > 0) {
      const orgManagers = currentManagers
        .filter(m => m.userType === 'org')
        .map(m => m.user as OrgUser);
      const tenantManagers = currentManagers
        .filter(m => m.userType === 'tenant')
        .map(m => m.user as Tenant);

      let org = orgManagers;
      let tenant = tenantManagers;
      if (allowMultiple && orgManagers.length + tenantManagers.length > MAX_CALL_RECIPIENTS) {
        const nextOrg: OrgUser[] = [];
        const nextTenant: Tenant[] = [];
        let remaining = MAX_CALL_RECIPIENTS;
        for (const u of orgManagers) {
          if (remaining <= 0) break;
          nextOrg.push(u);
          remaining--;
        }
        for (const u of tenantManagers) {
          if (remaining <= 0) break;
          nextTenant.push(u);
          remaining--;
        }
        org = nextOrg;
        tenant = nextTenant;
        message.warning(
          `This device has more than ${MAX_CALL_RECIPIENTS} recipients. Only the first ${MAX_CALL_RECIPIENTS} are shown; adjust the list as needed.`
        );
      }

      setSelectedOrgUsers(org);
      setSelectedTenants(tenant);

      // Set active tab based on which type has selections
      if (org.length > 0) {
        setActiveTab(0);
      } else if (tenant.length > 0) {
        setActiveTab(1);
      }
    } else if (open) {
      setSelectedOrgUsers([]);
      setSelectedTenants([]);
      setActiveTab(0);
    }
  }, [open, currentManagers, allowMultiple]);

  // Fetch org users when modal opens or when org tab is active
  // Use apartmentServiceGetPermissions API to get users with apartment permissions
  useEffect(() => {
    if (open && (activeTab === 0 || currentManagers.some(m => m.userType === 'org'))) {
      setLoadingOrgUsers(true);

      if (apartmentId) {
        // Use only the apartment permissions API as the source of users
        apartmentServiceGetPermissions(apartmentId)
          .then(permissionsRes => {
            const permittedUsers = permissionsRes.data?.data || [];

            // Convert permission users directly to OrgUser format
            const orgUsersFromPermissions = permittedUsers
              .map(p => ({
                id: p.user?.id,
                email: p.user?.email,
                name: `${p.user?.firstName || ''} ${p.user?.lastName || ''}`.trim(),
                firstName: p.user?.firstName,
                lastName: p.user?.lastName,
                status: p.user?.status || 'active',
              }))
              .filter(user => user.id && user.email); // Filter out invalid users

            setOrgUsers(orgUsersFromPermissions as OrgUser[]);
          })
          .catch(() => {
            message.error('Failed to load org users');
          })
          .finally(() => setLoadingOrgUsers(false));
      } else {
        // If no apartmentId, fetch all org users (original behavior)
        orgUserServiceFind(undefined, orgId, 1000, 1)
          .then(res => {
            setOrgUsers(res.data?.users || []);
          })
          .catch(() => {
            message.error('Failed to load org users');
          })
          .finally(() => setLoadingOrgUsers(false));
      }
    }
  }, [open, orgId, apartmentId, activeTab, currentManagers]);

  // Fetch tenants when modal opens or when tenant tab is active
  // Filter tenants by apartmentId if provided (same property as the device)
  useEffect(() => {
    if (open && (activeTab === 1 || currentManagers.some(m => m.userType === 'tenant'))) {
      setLoadingTenants(true);

      if (apartmentId) {
        // Filter tenants by the apartment/property that the device is associated with
        tenantServiceFind(undefined, apartmentId, 1000, 1, orgId)
          .then(res => {
            setTenants(res.data?.tenants || []);
          })
          .catch(() => {
            message.error('Failed to load tenants');
          })
          .finally(() => setLoadingTenants(false));
      } else {
        // If no apartmentId, fetch all tenants (original behavior)
        tenantServiceFind(undefined, undefined, 1000, 1, orgId)
          .then(res => {
            setTenants(res.data?.tenants || []);
          })
          .catch(() => {
            message.error('Failed to load tenants');
          })
          .finally(() => setLoadingTenants(false));
      }
    }
  }, [open, orgId, apartmentId, activeTab, currentManagers]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleConfirm = async () => {
    const totalSelected = selectedOrgUsers.length + selectedTenants.length;

    if (totalSelected === 0) {
      message.error('Please select at least one user');
      return;
    }

    if (allowMultiple && totalSelected > MAX_CALL_RECIPIENTS) {
      message.error(`You can select at most ${MAX_CALL_RECIPIENTS} recipients.`);
      return;
    }

    // If bulk operations are enabled and deviceId is provided, call the API directly
    if (enableBulkOperations && deviceId) {
      setIsSubmitting(true);
      try {
        // Collect user IDs from both org users and tenants
        const orgUserIds = selectedOrgUsers.map(user => user.id).filter(Boolean) as string[];

        const tenantUserIds = selectedTenants
          .map(tenant => {
            // Try to get the user ID from tenant.user.id first, fallback to tenant.id
            if (tenant.user?.id) {
              return tenant.user.id;
            }
            return tenant.id;
          })
          .filter(Boolean) as string[];

        // Combine all user IDs
        const allUserIds = [...orgUserIds, ...tenantUserIds];

        if (allUserIds.length === 0) {
          message.error('No valid user IDs found');
          return;
        }

        if (isEditMode) {
          // In edit mode, always replace all existing managers
          const replaceReq: IntercomManagerReplaceReq = {userIds: allUserIds};
          await replaceIntercomManagers(deviceId, replaceReq);
          message.success(`Successfully updated managers with ${allUserIds.length} user(s)`);
        } else {
          // In add mode, use bulk assign
          const bulkReq: IntercomManagerBulkAssignReq = {userIds: allUserIds};
          const response = await bulkAssignIntercomManagers(deviceId, bulkReq);

          if (response.data?.success) {
            const succeeded = response.data.results?.succeeded?.length || 0;
            const failed = response.data.results?.failed?.length || 0;

            if (failed > 0) {
              message.warning(
                `Assigned ${succeeded} of ${succeeded + failed} managers. ${failed} failed.`
              );
            } else {
              message.success(`Successfully assigned ${succeeded} manager(s)`);
            }
          }
        }

        // Call onRefresh instead of onConfirm to avoid double API calls
        if (onRefresh) {
          onRefresh();
        }
        onClose();
      } catch (error: any) {
        message.error(`Failed to ${isEditMode ? 'update' : 'assign'} managers: ${error.message}`);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      // Fall back to the original callback approach
      // For backwards compatibility, if only one type is selected, use that
      if (selectedOrgUsers.length > 0 && selectedTenants.length === 0) {
        onConfirm(selectedOrgUsers, 'org', isEditMode);
      } else if (selectedTenants.length > 0 && selectedOrgUsers.length === 0) {
        onConfirm(selectedTenants, 'tenant', isEditMode);
      } else {
        // Both types selected - combine them (this may need special handling in parent)
        const allUsers = [...selectedOrgUsers, ...selectedTenants];
        onConfirm(allUsers, 'org', isEditMode); // Default to 'org' type when mixed
      }
    }
  };

  const handleClearAllClick = () => {
    if (!deviceId || !enableBulkOperations) return;
    setClearConfirmOpen(true);
  };

  const handleConfirmClearAll = async () => {
    if (!deviceId || !enableBulkOperations) return;

    setIsSubmitting(true);
    try {
      await replaceIntercomManagers(deviceId, {userIds: []});
      message.success('Cleared all recipients successfully.');
      setClearConfirmOpen(false);
      if (onRefresh) {
        onRefresh();
      }
      onClose();
    } catch (error: any) {
      message.error(`Failed to clear recipients: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getSelectedUsers = () => {
    // Return all selected users from both tabs combined
    return [...selectedOrgUsers, ...selectedTenants];
  };

  const getTotalSelectedCount = () => {
    return selectedOrgUsers.length + selectedTenants.length;
  };

  const onOrgSelectionChange = (_: unknown, val: OrgUser | OrgUser[] | null) => {
    if (allowMultiple) {
      const next = Array.isArray(val) ? val : [];
      const maxForOrg = Math.max(0, MAX_CALL_RECIPIENTS - selectedTenants.length);
      if (next.length > maxForOrg) {
        message.warning(RECIPIENTS_LIMIT_TOOLTIP);
        setSelectedOrgUsers(next.slice(0, maxForOrg));
      } else {
        setSelectedOrgUsers(next);
      }
    } else {
      setSelectedOrgUsers(val ? [val as OrgUser] : []);
    }
  };

  const onTenantSelectionChange = (_: unknown, val: Tenant | Tenant[] | null) => {
    if (allowMultiple) {
      const next = Array.isArray(val) ? val : [];
      const maxForTenant = Math.max(0, MAX_CALL_RECIPIENTS - selectedOrgUsers.length);
      if (next.length > maxForTenant) {
        message.warning(RECIPIENTS_LIMIT_TOOLTIP);
        setSelectedTenants(next.slice(0, maxForTenant));
      } else {
        setSelectedTenants(next);
      }
    } else {
      setSelectedTenants(val ? [val as Tenant] : []);
    }
  };

  const isOptionDisabledAtLimit = (optionId: string | undefined, tab: 'org' | 'tenant') => {
    if (!allowMultiple || !optionId) return false;
    const inSelected =
      tab === 'org'
        ? selectedOrgUsers.some(o => o.id === optionId)
        : selectedTenants.some(t => t.id === optionId);
    if (inSelected) return false;
    return getTotalSelectedCount() >= MAX_CALL_RECIPIENTS;
  };

  const formatOrgUserLabel = (user: OrgUser) => {
    return `${user.name || ''} (${user.email || ''})`;
  };

  const formatTenantLabel = (tenant: Tenant) => {
    const name = `${tenant.firstName || ''} ${tenant.lastName || ''}`.trim();
    const unit = tenant.room?.name ? `Unit: ${tenant.room.name}` : '';
    const email = tenant.email || '';

    return `${name} ${unit ? `- ${unit}` : ''} (${email})`;
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={(event, reason) => {
        // Prevent closing when clicking outside (backdrop) or pressing escape
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          return;
        }
        onClose();
      }}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown
      sx={{
        '& .MuiDialog-paper': {
          minWidth: 500,
          maxWidth: 700,
          minHeight: 500,
          maxHeight: '80vh',
        },
      }}
      TransitionProps={{
        onExited: onExited,
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
          <Typography variant="h6">{title}</Typography>
          {allowMultiple && (
            <Tooltip title={RECIPIENTS_LIMIT_TOOLTIP} arrow placement="top">
              <Chip
                icon={<InfoOutlinedIcon sx={{fontSize: '1rem !important'}} />}
                label={`${getTotalSelectedCount()} / ${MAX_CALL_RECIPIENTS} selected`}
                color="primary"
                size="small"
                variant={getTotalSelectedCount() > 0 ? 'filled' : 'outlined'}
              />
            </Tooltip>
          )}
        </Box>
      </DialogTitle>
      <DialogContent>
        {isEditMode && allowMultiple && enableBulkOperations && (
          <Box sx={{mb: 2}}>
            <Alert severity="warning" sx={{mt: 1}}>
              Click update will replace all current recipients with your selection.
            </Alert>
          </Box>
        )}

        <Box sx={{borderBottom: 1, borderColor: 'divider', mb: 2}}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label="user type tabs"
            variant="fullWidth"
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
              },
              '& .Mui-selected': {
                color: 'primary.main',
              },
            }}
          >
            <Tab
              label={`Organization Users${
                selectedOrgUsers.length > 0 ? ` (${selectedOrgUsers.length})` : ''
              }`}
              id="user-tab-0"
              aria-controls="user-tabpanel-0"
              disabled={loadingOrgUsers && activeTab !== 0}
            />
            <Tab
              label={`Residents Users${
                selectedTenants.length > 0 ? ` (${selectedTenants.length})` : ''
              }`}
              id="user-tab-1"
              aria-controls="user-tabpanel-1"
              disabled={loadingTenants && activeTab !== 1}
            />
          </Tabs>
        </Box>

        {/* Add the TabPanel components and rest of the content */}
        <TabPanel value={activeTab} index={0}>
          <Autocomplete
            multiple={allowMultiple}
            size="small"
            options={orgUsers}
            getOptionLabel={formatOrgUserLabel}
            loading={loadingOrgUsers}
            value={allowMultiple ? selectedOrgUsers : selectedOrgUsers[0] || null}
            onChange={onOrgSelectionChange}
            getOptionDisabled={option => isOptionDisabledAtLimit(option.id, 'org')}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderTags={(value, getTagProps) =>
              allowMultiple
                ? value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={formatOrgUserLabel(option)}
                      {...getTagProps({index})}
                      key={option.id}
                    />
                  ))
                : null
            }
            renderInput={params => (
              <TextField
                {...params}
                variant="outlined"
                placeholder={`Select Organization User${allowMultiple ? 's' : ''}`}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingOrgUsers ? <CircularProgress color="inherit" size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            sx={{minWidth: 300}}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <Autocomplete
            multiple={allowMultiple}
            size="small"
            options={tenants}
            getOptionLabel={formatTenantLabel}
            loading={loadingTenants}
            value={allowMultiple ? selectedTenants : selectedTenants[0] || null}
            onChange={onTenantSelectionChange}
            getOptionDisabled={option => isOptionDisabledAtLimit(option.id, 'tenant')}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderTags={(value, getTagProps) =>
              allowMultiple
                ? value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={formatTenantLabel(option)}
                      {...getTagProps({index})}
                      key={option.id}
                    />
                  ))
                : null
            }
            renderInput={params => (
              <TextField
                {...params}
                variant="outlined"
                placeholder={`Select Resident User${allowMultiple ? 's' : ''}`}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingTenants ? <CircularProgress color="inherit" size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            sx={{minWidth: 300}}
          />
        </TabPanel>
      </DialogContent>
      <DialogActions sx={{justifyContent: 'space-between', px: 3}}>
        {isEditMode && enableBulkOperations && deviceId && getTotalSelectedCount() > 0 ? (
          <Button
            color="warning"
            variant="outlined"
            onClick={handleClearAllClick}
            disabled={isSubmitting}
          >
            Clear All
          </Button>
        ) : (
          <Box />
        )}
        <Box sx={{display: 'flex', gap: 1}}>
          <Button onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={
              getSelectedUsers().length === 0 ||
              isSubmitting ||
              (allowMultiple && getTotalSelectedCount() > MAX_CALL_RECIPIENTS)
            }
            onClick={handleConfirm}
          >
            {isSubmitting ? <CircularProgress size={20} /> : confirmButtonText}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
    <Modal
      open={clearConfirmOpen}
      title="Clear All Recipients"
      confirmText="Clear All"
      cancelText="Cancel"
      loading={isSubmitting}
      handleCancel={() => {
        if (!isSubmitting) {
          setClearConfirmOpen(false);
        }
      }}
      handleConfirm={handleConfirmClearAll}
      modalContent={
        <Typography>
          Are you sure you want to clear all call recipients for this device?
        </Typography>
      }
    />
  </>
  );
}
